import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  DataDocs,
  SubdocumenstWithParentId,
  SubdocumentWithParentId
} from "../../types/tables-data";
import {
  generateDocumentUrls,
  generateSingleDocumentUrl
} from "./documentsThunk";
import { ExtractedData } from "./types";

/**
 * The case's documents as a flat list.
 *
 * Ordering and grouping used to live here, mirroring the Firestore
 * `documentsTableOrder` tree. Both now belong to the case filesystem
 * (`documents/{individualId}/fsnodes`), which the Miller-column browser owns,
 * so this slice is only the document records themselves — the shared cache the
 * upload flows, AI modals and drafting dependencies read from.
 */
export interface DocumentState {
  allDocuments: DataDocs[];
  extractedData: ExtractedData | null;
  isLoadingDocuments: boolean | null;
  isUploadingDocuments?: boolean;
  corruptRemovedNames?: string[];
}

const initialState: DocumentState = {
  allDocuments: [],
  extractedData: null,
  isLoadingDocuments: null,
  isUploadingDocuments: false,
  corruptRemovedNames: []
};

export interface DocumentReducer {
  document: DocumentState;
}

export interface SubDocumentPayload {
  data: SubdocumentWithParentId;
}

export interface SubDocumentsPayload {
  data: SubdocumenstWithParentId;
}

const findDocument = (state: DocumentState, id: string | undefined) =>
  id ? state.allDocuments.find((document) => document.id === id) : undefined;

const upsert = (state: DocumentState, updated: DataDocs) => {
  const existing = findDocument(state, updated.id);
  if (!existing) {
    state.allDocuments.push(updated);
    return;
  }
  // Keep the locally-resolved blob URL and loaded subdocuments: a Firestore
  // snapshot carries neither, and dropping them would blank the preview.
  Object.assign(existing, {
    ...updated,
    sub_documents: existing.sub_documents ?? updated.sub_documents,
    docUrl: existing.docUrl ?? updated.docUrl
  });
};

export const documentSlice = createSlice({
  name: "document",
  initialState,
  reducers: {
    setAllDocuments: (state, action: PayloadAction<DataDocs[]>) => {
      state.allDocuments = [...action.payload];
    },
    addNewDocument: (state, action: PayloadAction<DataDocs>) => {
      if (!findDocument(state, action.payload.id)) {
        state.allDocuments.push(action.payload);
      }
    },
    updateDocument: (state, action: PayloadAction<DataDocs>) => {
      upsert(state, action.payload);
    },
    updateDocuments: (state, action: PayloadAction<DataDocs[]>) => {
      action.payload.forEach((document) => upsert(state, document));
    },
    deleteDocument: (state, action: PayloadAction<DataDocs>) => {
      state.allDocuments = state.allDocuments.filter(
        (document) => document.id !== action.payload.id
      );
    },
    setSubDocuments: (state, action: PayloadAction<SubDocumentsPayload>) => {
      const {
        data: { documents, id }
      } = action.payload;
      const document = findDocument(state, id);
      if (document) document.sub_documents = documents;
    },
    addSubDocument: (state, action: PayloadAction<SubDocumentPayload>) => {
      const { data } = action.payload;
      const document = findDocument(state, data.id);
      if (!document) return;
      if (!document.sub_documents) document.sub_documents = [];
      document.sub_documents.push(data.document as DataDocs);
    },
    setIsLoadingDocuments: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDocuments = action.payload;
    },
    setIsUploadingDocuments: (state, action: PayloadAction<boolean>) => {
      state.isUploadingDocuments = action.payload;
    },
    addCorruptRemovedNames: (state, action: PayloadAction<string[]>) => {
      const existing = new Set(state.corruptRemovedNames ?? []);
      for (const name of action.payload) existing.add(String(name));
      state.corruptRemovedNames = Array.from(existing);
    },
    setExtractedDataD: (state, action: PayloadAction<ExtractedData | null>) => {
      state.extractedData = action.payload;
    },
    clearAllDocuments: () => ({ ...initialState })
  },
  extraReducers: (builder) => {
    builder.addCase(generateDocumentUrls.fulfilled, (state, action) => {
      action.payload.forEach(({ id, fileUrl, mainDocId }) => {
        if (mainDocId) {
          const subDocument = findDocument(
            state,
            mainDocId
          )?.sub_documents?.find((candidate) => candidate.id === id);
          if (subDocument) subDocument.docUrl = fileUrl;
          return;
        }
        const document = findDocument(state, id);
        if (document) document.docUrl = fileUrl;
      });
    });

    builder.addCase(generateSingleDocumentUrl.fulfilled, (state, action) => {
      const { id, fileUrl } = action.payload;
      const document = findDocument(state, id);
      if (document) document.docUrl = fileUrl;
    });
  }
});

export const {
  setAllDocuments,
  addNewDocument,
  updateDocument,
  updateDocuments,
  deleteDocument,
  setSubDocuments,
  addSubDocument,
  setIsLoadingDocuments,
  setIsUploadingDocuments,
  addCorruptRemovedNames,
  setExtractedDataD,
  clearAllDocuments
} = documentSlice.actions;

export default documentSlice.reducer;
